//@flow
import Request from 'superagent';
import SessionStore from '../Stores/Session/session';
import Constants from '../constants';
import Dispatcher from '../dispatcher';
import APIResponseReader from './api-response-reader';
import GlobalData from './globalData';
import type { Response } from './api-response-reader';

const TIMEOUT = 30000;

type VersionInfo = {
  baseUrl:string,
  responseReaderClass:Class<APIResponseReader>,
  requestMiddleware:(req:Object) => void,
};

const VERSION_INFO = {
  'v2': {
    baseUrl: '/ipa/v2/membership/',
    responseReaderClass: APIResponseReader,
    requestMiddleware: _defaultConfig,
  },
};

export type VersionNumber = $Keys<typeof VERSION_INFO>;

const _pendingRequests:{[string]:boolean} = {};

function _defaultConfig(request) {
  request.type('application/json')
    .set('Accept', 'application/json');
}

// for any authenticated requests we're going to ensure no caching
function _authConfig(request) {
  const token = SessionStore.getToken();
  if (token) {
    request.set('Authorization', 'Bearer ' + token);
  }
  // apply a cache busting query parameter to avoid IE caching behavior
  request.query(Date.now().toString());
}

export type ResponsePromiseHandler = (res:APIResponseReader) => Promise<APIResponseReader>|APIResponseReader;

export class ApiRequest {
  _currentPromise:?Promise<APIResponseReader>;

  constructor(promise?:Promise<*>) {
    this._currentPromise = promise;
  }

  _then(onFulfill:ResponsePromiseHandler, onReject?:ResponsePromiseHandler):ApiRequest {
    this._currentPromise = this._currentPromise && this._currentPromise.then(onFulfill, onReject);
    return this;
  }

  _catch(onReject:ResponsePromiseHandler):ApiRequest {
    this._currentPromise = this._currentPromise && this._currentPromise.catch(onReject);
    return this;
  }

  _dispatch(key:string, response:APIResponseReader):APIResponseReader {
    Dispatcher.dispatchAction(key, response);
    return response;
  }

  // Dispatch an error condition using a standard format.  The upside to this
  // format is that it is fully backwards compatible but also makes it easy to
  // map to a format that makes it easier to track where errors came from
  _dispatchError(key:string, response:APIResponseReader):APIResponseReader {
    const type = response.getErrorType() || Constants.ERROR;
    Dispatcher.dispatchAction(type, response, {
      originalAction: key,
      type,
    });
    return response;
  }

  dispatch(key:string, errorKey?:string):ApiRequest {
    const onFulfill = (resp:APIResponseReader) => this._dispatch(key, resp);
    const onReject = (resp:APIResponseReader) => Promise.reject(this._dispatchError(errorKey || key, resp));

    return this._then(onFulfill, onReject);
  }

  dispatchError(key:string):ApiRequest {
    const onReject = (resp:APIResponseReader) => Promise.reject(this._dispatchError(key, resp));
    return this._catch(onReject);
  }

  then(onFulfill:ResponsePromiseHandler, onReject?:ResponsePromiseHandler):ApiRequest {
    return this._then(onFulfill, onReject);
  }

  thenCall(request:(res:APIResponseReader) => ApiRequest) {
    return this._then((res) => request(res)._promise());
  }

  catch(onReject:ResponsePromiseHandler):ApiRequest {
    return this._catch(onReject);
  }

  finally(onAny:ResponsePromiseHandler):ApiRequest {
    return this._then(onAny, onAny);
  }

  onSuccess(action:(res:APIResponseReader) => mixed):ApiRequest {
    return this._then((res) => {
      action(res);
      return res;
    });
  }

  onFailure(action:(res:APIResponseReader) => mixed):ApiRequest {
    return this._catch((res) => {
      action(res);
      return Promise.reject(res);
    });
  }

  _promise():Promise<APIResponseReader> {
    return this._currentPromise ||
      Promise.reject(this._errorResponse(Constants.ERROR));
  }

  _errorResponse(error:string):APIResponseReader {
    return new APIResponseReader(null, error);
  }
}

type HTTPMethod = 'GET'|'POST'|'PUT'|'DELETE';

class ApiBuilder {
  _url:string;
  _parallelRequests:boolean = false;
  _authenticated:boolean = true;
  _softAuthenticated:boolean = false;
  _addAuth:boolean = true;
  _version:VersionNumber = 'v2';
  _timeout:number = TIMEOUT;
  _method:HTTPMethod = 'GET';
  _name:?string = null;

  constructor(url?:string) {
    this._url = url || '';
  }

  name(name:string) {
    this._name = name;
    return this;
  }

  noauth():ApiBuilder {
    this._authenticated = false;
    this._addAuth = false;
    return this;
  }

  optionalAuth():ApiBuilder {
    this._authenticated = false;
    return this;
  }

  softAuth():ApiBuilder{
    this._authenticated = false;
    this._softAuthenticated = true;
    return this;
  }

  timeout(timeout:number):ApiBuilder {
    this._timeout = timeout;
    return this;
  }

  parallel():ApiBuilder {
    this._parallelRequests = true;
    return this;
  }

  get():ApiRequest {
    this._method = 'GET';
    return this._buildRequest();
  }

  del():ApiRequest {
    this._method = 'DELETE';
    return this._buildRequest();
  }

  post(body:Object|Array<*>):ApiRequest {
    this._method = 'POST';
    return this._buildRequest(body);
  }

  put(body:Object|Array<*>):ApiRequest {
    this._method = 'PUT';
    return this._buildRequest(body);
  }

  _getVersionInfo():VersionInfo {
    return VERSION_INFO[this._version];
  }

  _getFullUrl():string {
    if (this._url.indexOf("http") >= 0) {
      return this._url;
    }

    const { baseUrl } = this._getVersionInfo();
    return baseUrl + this._url;
  }

  _skipRequest():boolean {
    if (global.inServer || !this._url) {
      return true;
    }

    if (this._method === 'GET') {
      // Don't perform request if same request is pending.
      if (!this._parallelRequests && _pendingRequests[this._url]) return true;
    }

    const loggedIn = SessionStore.isLoggedIn();
    if (this._authenticated && !loggedIn) return true;

    if (this._softAuthenticated && !(loggedIn || SessionStore.isSoftAuth())) return true;

    return false;
  }

  _clearPendingIfNecessary() {
    if (this._method === 'GET' && _pendingRequests[this._url]) {
      _pendingRequests[this._url] = false;
    }
  }

  /*
   * This is the standard response handling that includes error processing
   * code to handle cases that the caller shouldn't care about like different
   * http codes
   */
  _handleResponse(resolve:(res:APIResponseReader) => void,
    reject:(res:APIResponseReader) => void,
    err:?Object,
    response:Response) {
    let errorKey = null;

    if (err || (response && !response.ok)) {
      errorKey = Constants.ERROR;

      if (err && err.timeout === TIMEOUT) {
        errorKey = Constants.TIMEOUT;
      }
      else if (response && (response.status === 401 || response.status === 403)) {
        errorKey = Constants.NOT_AUTHORIZED;
      }
    }

    const RespReader = this._getVersionInfo().responseReaderClass;

    // the response reader has any version specific response body handling
    const resp = new RespReader(response, errorKey);

    this._clearPendingIfNecessary();

    if (errorKey) {
      reject(resp);
    }
    else {
      resolve(resp);
    }
  }

  _getStubResponse():?APIResponseReader {
    const data = this._name && GlobalData.get('apiData', this._name);
    if (!data) return null;

    return stubApiResponse(this._version, data, this._url, this._method);
  }

  _buildPromise(data?:Object|Array<*>):Promise<APIResponseReader> {
    const stubResponse = this._getStubResponse();
    if (stubResponse) {
      return Promise.resolve(stubResponse);
    }

    const versionInfo = this._getVersionInfo();

    const request = Request(this._method, this._getFullUrl());
    if (data) {
      request.send(data);
    }
    request.timeout(this._timeout);
    request.use(versionInfo.requestMiddleware);
    if (this._addAuth) {
      request.use(_authConfig);
    }

    // bind resolve and reject to the _handleResponse call so that the
    // generated function just gets the error and response from the request
    // eslint-disable-next-line
    return new Promise((resolve, reject) => {
      request.end(this._handleResponse.bind(this, resolve, reject));
    });
  }

  _buildRequest(data?:Object|Array<*>) {
    if (this._skipRequest()) {
      return new ApiRequest();
    }

    if (!this._parallelRequests && this._method === 'GET') {
      _pendingRequests[this._url] = true;
    }

    return new ApiRequest(this._buildPromise(data));
  }
}

export default function api(...path:mixed[]) {
  return new ApiBuilder(path && path.join('/'));
}

export function stubApiResponse(version:VersionNumber, data:any, path:string = 'stub', method:string = 'GET', reqData:Object = {}, responseLoc:?string, ok:boolean = true):APIResponseReader {
  const versionInfo = VERSION_INFO[version];
  const ReaderClass = versionInfo.responseReaderClass;
  const url = versionInfo.baseUrl + path + '?' + Date.now().toString();
  const location =  responseLoc ? versionInfo.baseUrl + responseLoc : null;

  const body = ok ? { body: data } : data;

  return new ReaderClass({
    body,
    req: {
      url,
      path:url,
      method,
      _data: reqData,
      headers:{},
    },
    headers: {
      location,
    },
    ok,
  });
}
